// src/components/communications/TranscriptionSection.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { FaFileAlt, FaSpinner, FaChevronDown, FaChevronUp, FaRedo, FaExclamationTriangle } from 'react-icons/fa';
import { transcribeCall, getTranscription } from '../../services/communicationService';

const Container = styled.div`
  margin-top: ${props => props.theme.spacing(2)};
  padding-top: ${props => props.theme.spacing(2)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}22;
`;

const TranscribeButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    opacity: 0.9;
    background-color: ${props => props.theme.colors.primary}22;
    color: ${props => props.theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProcessingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TranscriptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: ${props => props.theme.spacing(0.5)} 0;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const TranscriptLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  font-size: 0.85rem;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
`;

const TranscriptMeta = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: normal;
  margin-left: ${props => props.theme.spacing(1)};
`;

const TranscriptText = styled.div`
  margin-top: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1.5)};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  font-size: 0.85rem;
  line-height: 1.5;
  white-space: pre-wrap;
  color: ${props => props.theme.colors.textPrimary};
  max-height: 300px;
  overflow-y: auto;
`;

const TranscriptActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing(1)};
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: 0.85rem;
  color: ${props => props.theme.colors.error};
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const TranscriptionSection = ({ callId, transcription: initialTranscription, transcriptionStatus: initialStatus, transcribedAt: initialTranscribedAt }) => {
  const [status, setStatus] = useState(initialStatus || 'none');
  const [transcription, setTranscription] = useState(initialTranscription || '');
  const [transcribedAt, setTranscribedAt] = useState(initialTranscribedAt || null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const data = await getTranscription(callId);
        if (data.transcription_status === 'completed') {
          setStatus('completed');
          setTranscription(data.transcription);
          setTranscribedAt(data.transcribed_at);
          setExpanded(true);
          stopPolling();
        } else if (data.transcription_status === 'failed') {
          setStatus('failed');
          stopPolling();
        }
      } catch (error) {
        console.error('Error polling transcription:', error);
      }
    }, 3000);
  }, [callId, stopPolling]);

  // Start polling if status is pending/processing on mount
  useEffect(() => {
    if (status === 'pending' || status === 'processing') {
      startPolling();
    }
    return stopPolling;
  }, [status, startPolling, stopPolling]);

  // Update from props if they change
  useEffect(() => {
    if (initialStatus) setStatus(initialStatus);
    if (initialTranscription) setTranscription(initialTranscription);
    if (initialTranscribedAt) setTranscribedAt(initialTranscribedAt);
  }, [initialStatus, initialTranscription, initialTranscribedAt]);

  const handleTranscribe = async () => {
    setLoading(true);
    try {
      await transcribeCall(callId);
      setStatus('pending');
      startPolling();
    } catch (error) {
      console.error('Failed to start transcription:', error);
      setStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  // Status: none - show transcribe button
  if (status === 'none') {
    return (
      <Container>
        <TranscribeButton onClick={handleTranscribe} disabled={loading}>
          {loading ? <FaSpinner /> : <FaFileAlt />}
          {loading ? 'Starting...' : 'Transcribe'}
        </TranscribeButton>
      </Container>
    );
  }

  // Status: pending/processing - show spinner
  if (status === 'pending' || status === 'processing') {
    return (
      <Container>
        <ProcessingIndicator>
          <FaSpinner />
          Transcribing...
        </ProcessingIndicator>
      </Container>
    );
  }

  // Status: failed - show error with retry
  if (status === 'failed') {
    return (
      <Container>
        <ErrorMessage>
          <FaExclamationTriangle />
          Transcription failed
        </ErrorMessage>
        <TranscribeButton onClick={handleTranscribe} disabled={loading}>
          {loading ? <FaSpinner /> : <FaRedo />}
          {loading ? 'Starting...' : 'Retry'}
        </TranscribeButton>
      </Container>
    );
  }

  // Status: completed - show collapsible transcript
  return (
    <Container>
      <TranscriptHeader onClick={() => setExpanded(!expanded)}>
        <TranscriptLabel>
          <FaFileAlt />
          Transcript
          {transcribedAt && (
            <TranscriptMeta>{formatDate(transcribedAt)}</TranscriptMeta>
          )}
        </TranscriptLabel>
        {expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
      </TranscriptHeader>

      {expanded && (
        <>
          <TranscriptText>{transcription}</TranscriptText>
          <TranscriptActions>
            <TranscribeButton onClick={handleTranscribe} disabled={loading}>
              {loading ? <FaSpinner /> : <FaRedo />}
              {loading ? 'Starting...' : 'Re-transcribe'}
            </TranscribeButton>
          </TranscriptActions>
        </>
      )}
    </Container>
  );
};

export default TranscriptionSection;
